import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Save, Loader2 } from "lucide-react";

const TOLERANCE_OPTIONS = ["rough", "standard", "precision", "ultra_precision"];
const GEOMETRY_OPTIONS = ["rotational", "prismatic", "sheet", "complex"];
const RISK_OPTIONS = ["low", "medium", "high"];
const ACCESSIBILITY_OPTIONS = ["easy", "medium", "difficult"];

function NumInput({ value, onChange, placeholder, testid, min = 0, step = "any" }) {
  return (
    <input
      type="number"
      min={min}
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-2.5 py-1.5 rounded bg-slate-950 border border-slate-700 text-sm text-slate-100 font-mono-data focus:border-blue-500 outline-none"
    />
  );
}

function TextInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-2.5 py-1.5 rounded bg-slate-950 border border-slate-700 text-sm text-slate-100 focus:border-blue-500 outline-none"
    />
  );
}

function Select({ value, options, onChange, testid }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      data-testid={testid}
      className="w-full px-2.5 py-1.5 rounded bg-slate-950 border border-slate-700 text-sm text-slate-100 focus:border-blue-500 outline-none"
    >
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-tech text-[10px] text-amber-300/80 mb-2 mt-3">{title}</div>
      {children}
    </div>
  );
}

export default function EditAnalysisDialog({ analysis, onClose, onSave }) {
  const { t } = useTranslation();
  const dims0 = analysis?.estimated_dimensions_mm || {};
  const im0 = analysis?.industrial_metrics || {};
  const mf0 = analysis?.manual_features || {};

  const [length, setLength] = useState(dims0.length_mm ?? null);
  const [width, setWidth] = useState(dims0.width_mm ?? null);
  const [height, setHeight] = useState(dims0.height_mm ?? null);
  const [diameter, setDiameter] = useState(dims0.diameter_mm ?? null);

  const [geometry, setGeometry] = useState(analysis?.geometry_type || "");
  const [tolerance, setTolerance] = useState(analysis?.tolerance_class || "");
  const [ra, setRa] = useState(analysis?.surface_finish_ra_um ?? null);
  const [partDescription, setPartDescription] = useState(analysis?.part_description || "");

  const [setupMin, setSetupMin] = useState(im0.setup_minutes ?? null);
  const [fixturingMin, setFixturingMin] = useState(im0.fixturing_minutes ?? null);
  const [toolsCount, setToolsCount] = useState(im0.tools_count ?? null);
  const [setupsCount, setSetupsCount] = useState(im0.setups_count ?? null);
  const [featureCount, setFeatureCount] = useState(im0.feature_count ?? null);
  const [toleranceCriticality, setToleranceCriticality] = useState(im0.tolerance_criticality || "");
  const [vibrationRisk, setVibrationRisk] = useState(im0.vibration_risk || "");
  const [chipEvac, setChipEvac] = useState(im0.chip_evacuation_difficulty || "");
  const [toolAccess, setToolAccess] = useState(im0.tool_accessibility || "");

  const [threadsExt, setThreadsExt] = useState(mf0.threads_external || "");
  const [threadsInt, setThreadsInt] = useState(mf0.threads_internal || "");
  const [grooves, setGrooves] = useState(mf0.grooves || "");
  const [keyways, setKeyways] = useState(mf0.keyways || "");
  const [knurling, setKnurling] = useState(mf0.knurling || "");
  const [chamfers, setChamfers] = useState(mf0.chamfers || "");
  const [otherFeatures, setOtherFeatures] = useState(mf0.other_features || "");

  // Material price override (€/kg). null/empty = use catalog price.
  const [materialPriceOverride, setMaterialPriceOverride] = useState(
    analysis?._material_price_override_eur_per_kg ?? null
  );
  const catalogPricePerKg = analysis?._material_price_catalog_eur_per_kg ?? null;

  // Weight overrides
  const [rawWeightOverride, setRawWeightOverride] = useState(
    analysis?._raw_weight_kg_override ?? null
  );
  const [finishedWeight, setFinishedWeight] = useState(
    analysis?._finished_weight_kg ?? null
  );
  const computedRawWeight = analysis?._computed_raw_weight_kg ?? null;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        estimated_dimensions_mm: {
          length_mm: length,
          width_mm: width,
          height_mm: height,
          diameter_mm: diameter,
        },
        geometry_type: geometry || null,
        tolerance_class: tolerance || null,
        surface_finish_ra_um: ra,
        part_description: partDescription || null,
        industrial_metrics: {
          ...(setupMin !== null && { setup_minutes: setupMin }),
          ...(fixturingMin !== null && { fixturing_minutes: fixturingMin }),
          ...(toolsCount !== null && { tools_count: toolsCount }),
          ...(setupsCount !== null && { setups_count: setupsCount }),
          ...(featureCount !== null && { feature_count: featureCount }),
          ...(toleranceCriticality && { tolerance_criticality: toleranceCriticality }),
          ...(vibrationRisk && { vibration_risk: vibrationRisk }),
          ...(chipEvac && { chip_evacuation_difficulty: chipEvac }),
          ...(toolAccess && { tool_accessibility: toolAccess }),
        },
        manual_features: {
          threads_external: threadsExt || null,
          threads_internal: threadsInt || null,
          grooves: grooves || null,
          keyways: keyways || null,
          knurling: knurling || null,
          chamfers: chamfers || null,
          other_features: otherFeatures || null,
        },
        material_price_override_eur_per_kg: materialPriceOverride,
        raw_weight_kg_override: rawWeightOverride,
        finished_weight_kg: finishedWeight,
      };
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t("editAnalysis.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" data-testid="edit-analysis-dialog">
      <div className="bg-slate-900 border border-slate-700 rounded-md w-full max-w-3xl my-6 shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <div>
            <div className="label-tech text-amber-300">// MANUAL_OVERRIDE</div>
            <h2 className="font-display font-bold text-lg text-slate-100">{t("editAnalysis.title")}</h2>
            <p className="text-xs text-slate-400 mt-1">{t("editAnalysis.subtitle")}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 p-1" data-testid="edit-analysis-close">
            <X size={18}/>
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto space-y-2">
          <Section title={t("editAnalysis.sectionDesc")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <TextInput value={partDescription} onChange={setPartDescription} placeholder={t("editAnalysis.descPlaceholder")} testid="edit-part-description"/>
              <Select value={geometry} options={GEOMETRY_OPTIONS} onChange={setGeometry} testid="edit-geometry"/>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionDims")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.length")}</div>
                <NumInput value={length} onChange={setLength} placeholder="L" testid="edit-length"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.width")}</div>
                <NumInput value={width} onChange={setWidth} placeholder="W" testid="edit-width"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.height")}</div>
                <NumInput value={height} onChange={setHeight} placeholder="H" testid="edit-height"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.diameter")}</div>
                <NumInput value={diameter} onChange={setDiameter} placeholder="Ø" testid="edit-diameter"/>
              </div>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionTol")}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.tolClass")}</div>
                <Select value={tolerance} options={TOLERANCE_OPTIONS} onChange={setTolerance} testid="edit-tolerance"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">Ra (µm)</div>
                <NumInput value={ra} onChange={setRa} placeholder={t("editAnalysis.raExample")} testid="edit-ra"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.tolCriticality")}</div>
                <Select value={toleranceCriticality} options={RISK_OPTIONS} onChange={setToleranceCriticality} testid="edit-tol-criticality"/>
              </div>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionSetup")}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.setupMin")}</div>
                <NumInput value={setupMin} onChange={setSetupMin} testid="edit-setup-min"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.fixturingMin")}</div>
                <NumInput value={fixturingMin} onChange={setFixturingMin} testid="edit-fixturing-min"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.tools")}</div>
                <NumInput value={toolsCount} onChange={setToolsCount} testid="edit-tools-count"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.resetups")}</div>
                <NumInput value={setupsCount} onChange={setSetupsCount} testid="edit-setups-count"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.featureCount")}</div>
                <NumInput value={featureCount} onChange={setFeatureCount} testid="edit-feature-count"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.vibrations")}</div>
                <Select value={vibrationRisk} options={RISK_OPTIONS} onChange={setVibrationRisk} testid="edit-vibration"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.chips")}</div>
                <Select value={chipEvac} options={RISK_OPTIONS} onChange={setChipEvac} testid="edit-chip"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.accessibility")}</div>
                <Select value={toolAccess} options={ACCESSIBILITY_OPTIONS} onChange={setToolAccess} testid="edit-tool-access"/>
              </div>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionMaterialCost")}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.customPrice")}</div>
                <NumInput
                  value={materialPriceOverride}
                  onChange={setMaterialPriceOverride}
                  placeholder={catalogPricePerKg ? t("editAnalysis.catalogPh", { price: catalogPricePerKg }) : t("editAnalysis.emptyCatalogPh")}
                  testid="edit-material-price"
                  step="0.01"
                />
              </div>
              <div className="text-xs text-slate-400 leading-relaxed md:col-span-2">
                {catalogPricePerKg
                  ? t("editAnalysis.catalogInfo", { price: catalogPricePerKg })
                  : t("editAnalysis.noCatalogInfo")}
              </div>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionWeight")}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.rawWeight")}</div>
                <NumInput
                  value={rawWeightOverride}
                  onChange={setRawWeightOverride}
                  placeholder={computedRawWeight ? t("editAnalysis.computedPh", { value: computedRawWeight }) : t("editAnalysis.emptyAutoPh")}
                  testid="edit-raw-weight"
                  step="0.001"
                />
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.finishedWeight")}</div>
                <NumInput
                  value={finishedWeight}
                  onChange={setFinishedWeight}
                  placeholder={t("editAnalysis.optionalInfoPh")}
                  testid="edit-finished-weight"
                  step="0.001"
                />
              </div>
              <div className="text-xs text-slate-400 leading-relaxed">
                {computedRawWeight
                  ? t("editAnalysis.weightInfo", { value: computedRawWeight })
                  : t("editAnalysis.weightInfoEmpty")}
              </div>
            </div>
          </Section>

          <Section title={t("editAnalysis.sectionManual")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.threadsExt")}</div>
                <TextInput value={threadsExt} onChange={setThreadsExt} placeholder={t("editAnalysis.threadsExtPh")} testid="edit-threads-ext"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.threadsInt")}</div>
                <TextInput value={threadsInt} onChange={setThreadsInt} placeholder={t("editAnalysis.threadsIntPh")} testid="edit-threads-int"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.grooves")}</div>
                <TextInput value={grooves} onChange={setGrooves} placeholder={t("editAnalysis.groovesPh")} testid="edit-grooves"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.keyways")}</div>
                <TextInput value={keyways} onChange={setKeyways} placeholder={t("editAnalysis.keywaysPh")} testid="edit-keyways"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.knurling")}</div>
                <TextInput value={knurling} onChange={setKnurling} placeholder={t("editAnalysis.knurlingPh")} testid="edit-knurling"/>
              </div>
              <div>
                <div className="label-tech text-[9px] mb-1">{t("editAnalysis.chamfers")}</div>
                <TextInput value={chamfers} onChange={setChamfers} placeholder={t("editAnalysis.chamfersPh")} testid="edit-chamfers"/>
              </div>
            </div>
            <div className="mt-2">
              <div className="label-tech text-[9px] mb-1">{t("editAnalysis.otherData")}</div>
              <textarea
                value={otherFeatures}
                onChange={(e) => setOtherFeatures(e.target.value)}
                rows={3}
                placeholder={t("editAnalysis.otherPh")}
                data-testid="edit-other-features"
                className="w-full px-2.5 py-2 rounded bg-slate-950 border border-slate-700 text-sm text-slate-100 focus:border-blue-500 outline-none resize-y"
              />
            </div>
          </Section>

          {error && (
            <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/40 text-xs text-red-300" data-testid="edit-analysis-error">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded text-sm bg-slate-800 text-slate-200 border border-slate-700 hover:border-slate-500" data-testid="edit-analysis-cancel">
            {t("common.cancel")}
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60" data-testid="edit-analysis-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {saving ? t("common.saving") : t("editAnalysis.saveRecalc")}
          </button>
        </div>
      </div>
    </div>
  );
}
